import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Check,
  CheckCircle2,
  Circle,
  Download,
  FileText,
  Landmark,
  Loader2,
} from 'lucide-react'

import { colors, fmt } from './utils'

type PackageKind = 'board' | 'lender'
type StepStatus = 'pending' | 'running' | 'complete'

interface PipelineStep {
  label: string
  status: StepStatus
  duration?: number
}

const BOARD_SECTIONS = [
  'Executive Summary',
  '13-Week Forecast',
  'Covenant Status',
  'Risk Assessment',
  'Recommended Actions',
]

const LENDER_SECTIONS = [
  'Covenant Compliance',
  'Cash Flow Detail',
  'Facility Utilization',
  'Borrowing Base',
  'Collateral Summary',
]

const PIPELINE_LABELS = [
  'Data Collection',
  'Analysis',
  'Narrative Generation',
  'Formatting',
  'Quality Review',
]

const EXPORT_HISTORY = [
  { type: 'Board Memo', date: '2026-03-28', generated_by: 'Sarah Mitchell', status: 'Delivered' as const },
  { type: 'Lender Package', date: '2026-03-25', generated_by: 'David Chen', status: 'Delivered' as const },
  { type: 'Board Memo', date: '2026-02-14', generated_by: 'Alex Rivera', status: 'Pending' as const },
  { type: 'Lender Package', date: '2026-01-30', generated_by: 'David Chen', status: 'Delivered' as const },
]

function stepDelayMs() {
  return 800 + Math.floor(Math.random() * 401)
}

function initialPipeline(): PipelineStep[] {
  return PIPELINE_LABELS.map(label => ({ label, status: 'pending' }))
}

function AgentPipelineVisualization({ steps }: { steps: PipelineStep[] }) {
  const totalMs = steps.reduce((sum, step) => sum + (step.duration ?? 0), 0)

  return (
    <div className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4">
      <div className="flex flex-wrap items-stretch gap-2 overflow-x-auto py-1">
        {steps.map((step, index) => (
          <div key={step.label} className="flex items-center gap-2">
            <div className="flex min-w-[108px] flex-col items-center gap-1.5">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-lg border"
                style={{
                  backgroundColor:
                    step.status === 'complete'
                      ? `${colors.status.green}18`
                      : step.status === 'running'
                        ? `${colors.accent.teal}22`
                        : colors.bg.surface,
                  borderColor:
                    step.status === 'complete'
                      ? `${colors.status.green}55`
                      : step.status === 'running'
                        ? `${colors.accent.teal}55`
                        : '#e5e7eb',
                }}
              >
                {step.status === 'complete' ? (
                  <Check className="h-4 w-4" style={{ color: colors.status.green }} strokeWidth={2.5} />
                ) : step.status === 'running' ? (
                  <Loader2 className="h-4 w-4 animate-spin" style={{ color: colors.accent.teal }} />
                ) : (
                  <Circle className="h-4 w-4 text-gray-300" strokeWidth={2} />
                )}
              </div>
              <span className="text-center text-[10px] font-medium leading-tight text-gray-600 dark:text-gray-300">
                {step.label}
              </span>
              {step.duration != null && (
                <span className="text-[9px] text-gray-400 dark:text-gray-500">
                  {(step.duration / 1000).toFixed(1)}s
                </span>
              )}
            </div>
            {index < steps.length - 1 && (
              <div
                className="mb-6 h-px w-5 shrink-0 self-center"
                style={{
                  backgroundColor:
                    steps[index].status === 'complete' ? colors.status.green : '#e5e7eb',
                  opacity: steps[index].status === 'complete' ? 0.45 : 1,
                }}
              />
            )}
          </div>
        ))}
      </div>
      {totalMs > 0 && (
        <p className="mt-2 text-right text-[10px] text-gray-400 dark:text-gray-500">
          Elapsed: {(totalMs / 1000).toFixed(1)}s
        </p>
      )}
    </div>
  )
}

function buildReportHtml(kind: PackageKind, period: string, reportDate: string): string {
  const isBoard = kind === 'board'
  const title = isBoard ? 'Board Memo' : 'Lender Package'
  const contents = isBoard ? BOARD_SECTIONS : LENDER_SECTIONS
  const sectionsHtml = contents
    .map(
      (section, index) => `
      <div class="section">
        <h2>${index + 1}. ${section}</h2>
        <p>This section covers ${section.toLowerCase()} for ${period}. Figures are illustrative and for demonstration only.</p>
        ${
          index === 0
            ? `
        <table>
          <thead><tr><th>Metric</th><th>Current</th><th>Prior</th><th>Change</th></tr></thead>
          <tbody>
            <tr><td>Cash</td><td>${fmt.compact(4_200_000)}</td><td>${fmt.compact(3_800_000)}</td><td class="positive">+${fmt.compact(400_000)}</td></tr>
            <tr><td>Revolver availability</td><td>${fmt.compact(6_100_000)}</td><td>${fmt.compact(5_900_000)}</td><td class="positive">+${fmt.compact(200_000)}</td></tr>
            <tr><td>DSCR</td><td>1.45x</td><td>1.38x</td><td class="positive">+0.07x</td></tr>
          </tbody>
        </table>`
            : `
        <div class="placeholder"><p>Supporting schedules and commentary for ${section} would appear here.</p></div>`
        }
      </div>`,
    )
    .join('\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>SEER - ${title} - ${period}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: system-ui, sans-serif; max-width: 880px; margin: 0 auto; padding: 40px 32px 56px; color: ${colors.text.primary}; background: #fff; line-height: 1.55; }
  .cover { text-align: center; padding: 56px 16px 40px; border-bottom: 3px solid ${colors.accent.teal}; margin-bottom: 32px; }
  .cover .badge { display: inline-block; background: ${colors.accent.teal}; color: #fff; padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 700; letter-spacing: 0.04em; margin-bottom: 16px; }
  .cover h1 { font-size: 28px; margin-bottom: 6px; }
  .cover .meta { font-size: 14px; color: ${colors.text.secondary}; }
  .cover .tag { margin-top: 20px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.2em; color: ${colors.text.muted}; }
  .toc { margin-bottom: 32px; padding: 24px; background: ${colors.bg.surface}; border-radius: 10px; }
  .toc h2 { font-size: 15px; color: ${colors.accent.teal}; margin-bottom: 12px; }
  .toc ol { padding-left: 22px; }
  .toc li { padding: 4px 0; font-size: 14px; }
  .section { margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #e5e7eb; }
  .section h2 { font-size: 17px; color: ${colors.accent.teal}; margin-bottom: 10px; }
  .section p { font-size: 14px; color: #374151; margin-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 13px; }
  thead { background: ${colors.accent.teal}; color: #fff; }
  th, td { padding: 9px 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
  th:not(:first-child), td:not(:first-child) { text-align: right; font-variant-numeric: tabular-nums; }
  .positive { color: ${colors.status.green}; font-weight: 600; }
  .placeholder { background: ${colors.bg.surface}; border: 1px dashed #d1d5db; border-radius: 8px; padding: 16px; text-align: center; color: ${colors.text.secondary}; font-size: 13px; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 2px solid ${colors.accent.teal}; font-size: 11px; color: ${colors.text.muted}; text-align: center; }
</style>
</head>
<body>
  <div class="cover">
    <div class="badge">SEER Cashflow</div>
    <h1>${title}</h1>
    <p class="meta">Period: ${period}</p>
    <p class="meta">Prepared ${reportDate}</p>
    <p class="tag">${isBoard ? 'Board use' : 'Lender distribution'}</p>
  </div>
  <div class="toc">
    <h2>Contents</h2>
    <ol>${contents.map(section => `<li>${section}</li>`).join('')}</ol>
  </div>
  ${sectionsHtml}
  <div class="footer">Simulated export generated locally in the browser.</div>
</body>
</html>`
}

function triggerHtmlDownload(html: string, filenameBase: string) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filenameBase}.html`
  link.click()
  URL.revokeObjectURL(url)
}

export default function BoardExportPage() {
  const [generating, setGenerating] = useState(false)
  const [generationComplete, setGenerationComplete] = useState(false)
  const [lastGeneratedType, setLastGeneratedType] = useState<string | null>(null)
  const [pipelineSteps, setPipelineSteps] = useState<PipelineStep[]>(() => initialPipeline())
  const activeKindRef = useRef<PackageKind | null>(null)

  const startGeneration = useCallback((kind: PackageKind) => {
    activeKindRef.current = kind
    setLastGeneratedType(kind === 'board' ? 'Board Memo' : 'Lender Package')
    setGenerationComplete(false)
    setPipelineSteps(initialPipeline())
    setGenerating(true)
  }, [])

  useEffect(() => {
    if (!generating) return

    let cancelled = false

    const run = async () => {
      for (let i = 0; i < PIPELINE_LABELS.length; i++) {
        setPipelineSteps(previous =>
          previous.map((step, idx) => ({
            ...step,
            status: idx < i ? 'complete' : idx === i ? 'running' : 'pending',
          })),
        )
        const delay = stepDelayMs()
        await new Promise<void>(resolve => {
          setTimeout(resolve, delay)
        })
        if (cancelled) return
        setPipelineSteps(previous =>
          previous.map((step, idx) =>
            idx === i ? { ...step, status: 'complete', duration: delay } : step,
          ),
        )
      }
      if (!cancelled) {
        setGenerating(false)
        setGenerationComplete(true)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [generating])

  const downloadReport = useCallback((kind: PackageKind) => {
    const period = 'Q1 2026'
    const reportDate = new Date().toISOString().slice(0, 10)
    const html = buildReportHtml(kind, period, reportDate)
    triggerHtmlDownload(html, `seer_${kind}_${reportDate}`)
  }, [])

  const previewSections =
    lastGeneratedType?.toLowerCase().includes('lender') ? LENDER_SECTIONS : BOARD_SECTIONS
  const previewTitle =
    lastGeneratedType?.toLowerCase().includes('lender') ? 'Lender-ready compliance deck' : 'Executive board package'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FileText className="h-6 w-6 shrink-0" style={{ color: colors.accent.teal }} />
          Board and Lender Export
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Generate executive reporting packages.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${colors.accent.teal}20` }}
            >
              <FileText className="h-5 w-5" style={{ color: colors.accent.teal }} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Board Memo
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                Board-ready cash flow summary with executive commentary and risk
                assessment.
              </p>
            </div>
          </div>
          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Sections
            </h3>
            <ul className="space-y-1.5">
              {BOARD_SECTIONS.map(item => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: colors.status.green }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            onClick={() => startGeneration('board')}
            disabled={generating}
            className="mt-auto flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            style={{ backgroundColor: colors.accent.teal }}
          >
            {generating && activeKindRef.current === 'board' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4" />
                Generate
              </>
            )}
          </button>
        </div>

        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${colors.accent.indigo}22` }}
            >
              <Landmark className="h-5 w-5" style={{ color: colors.accent.indigo }} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Lender Package
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                Covenant compliance package with detailed cash flow projections and
                facility utilization.
              </p>
            </div>
          </div>
          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Sections
            </h3>
            <ul className="space-y-1.5">
              {LENDER_SECTIONS.map(item => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: colors.status.green }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            onClick={() => startGeneration('lender')}
            disabled={generating}
            className="mt-auto flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            style={{ backgroundColor: colors.accent.indigo }}
          >
            {generating && activeKindRef.current === 'lender' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Landmark className="h-4 w-4" />
                Generate
              </>
            )}
          </button>
        </div>
      </div>

      {generating && (
        <div className="space-y-3 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-5 shadow-sm">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: colors.accent.teal }} />
            Running export pipeline...
          </h3>
          <AgentPipelineVisualization steps={pipelineSteps} />
        </div>
      )}

      {generationComplete && !generating && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-5 shadow-sm">
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Package ready
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {lastGeneratedType} — all pipeline steps finished.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                downloadReport(
                  lastGeneratedType?.toLowerCase().includes('lender')
                    ? 'lender'
                    : 'board',
                )
              }
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white"
              style={{ backgroundColor: colors.accent.teal }}
            >
              <Download className="h-4 w-4" />
              Download HTML
            </button>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Document Preview
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Static preview of the latest generated package.
            </p>
            <div
              className="mt-4 space-y-5 rounded-lg border border-gray-100 dark:border-dark-border p-6"
              style={{ backgroundColor: colors.bg.surface }}
            >
              <div className="border-b border-gray-200 pb-5 text-center">
                <div
                  className="mx-auto mb-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white"
                  style={{ backgroundColor: colors.accent.teal }}
                >
                  SEER Cashflow
                </div>
                <h2 className="text-lg font-bold text-gray-900">{lastGeneratedType ?? 'Export'}</h2>
                <p className="mt-1 text-sm text-gray-600">{previewTitle}</p>
                <p className="mt-2 text-xs text-gray-400">
                  {fmt.shortDate(new Date().toISOString())} — illustrative content
                </p>
              </div>
              <div>
                <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Table of contents
                </h4>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-700">
                  {previewSections.map(section => (
                    <li key={section}>{section}</li>
                  ))}
                </ol>
              </div>
              <div className="rounded-lg border border-dashed border-gray-200 bg-white p-4 text-sm text-gray-600">
                <p className="font-medium text-gray-800">Executive snapshot</p>
                <p className="mt-2 leading-relaxed">
                  Liquidity remains stable with cash at {fmt.compact(4_200_000)} and
                  revolver availability at {fmt.compact(6_100_000)}. Covenant headroom
                  is comfortable; no breaches in the simulated period.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface shadow-sm">
        <div className="border-b border-gray-100 dark:border-dark-border px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Export History
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-dark-border text-left text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Generated by</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {EXPORT_HISTORY.map(row => (
                <tr
                  key={`${row.type}-${row.date}`}
                  className="border-b border-gray-50 dark:border-dark-border hover:bg-gray-50/80 dark:hover:bg-dark-surface-2/40"
                >
                  <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                    {row.type}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                    {fmt.shortDate(row.date)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                    {row.generated_by}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      style={{
                        backgroundColor:
                          row.status === 'Delivered'
                            ? `${colors.status.green}22`
                            : `${colors.status.yellow}22`,
                        color:
                          row.status === 'Delivered'
                            ? colors.status.green
                            : colors.status.yellow,
                      }}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
